import { Alert } from "@mui/material";
import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { ChatStatus } from "@/shared/domain/chat/ChatRecord";
import { useCancelGeneration } from "@/shared/domain/chat/hooks/useCancelGeneration";
import { useChat } from "@/shared/domain/chat/hooks/useChat";
import { useSendMessage } from "@/shared/domain/chat/hooks/useSendMessage";
import { useStartChat } from "@/shared/domain/chat/hooks/useStartChat";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { useAIChat } from "./AIChatContext";
import styles from "./Chat.module.css";
import { ChatByIdView } from "./ChatByIdView";
import { ChatInputBox } from "./ChatInputBox";
import { NewChatView } from "./NewChatView";

/**
 * Chat component orchestrates all data fetching and mutations.
 * It conditionally renders NewChat vs ChatMessages based on whether chatId exists.
 * ChatInput is rendered as a pure form component with callbacks.
 */
export const Chat: React.FC = () => {
  const orgId = useCurrentOrgId();
  const { currentChatId, setCurrentChatId, context } = useAIChat();

  // Data fetching
  const chatQuery = useChat(currentChatId, {
    orgId,
    loadMessages: true,
  });
  const chat = chatQuery.data;

  // Mutations
  const startChatMutation = useStartChat();
  const sendMessageMutation = useSendMessage();
  const cancelGenerationMutation = useCancelGeneration();

  // Derived state
  const loading =
    chatQuery.data !== undefined &&
    chatQuery.data?.status !== ChatStatus.USER_TURN;

  // Mutation handlers
  const handleStartChat = async (message: string) => {
    const chatRecord = await startChatMutation.mutateAsync({
      message,
      orgId,
      context,
    });
    setCurrentChatId(chatRecord.chat_id);
    return chatRecord;
  };

  const handleSendMessage = async (message: string) => {
    if (!currentChatId) {
      return;
    }

    await sendMessageMutation.mutateAsync({
      chatId: currentChatId,
      message: message,
      orgId,
      context,
    });
  };

  // Callbacks for ChatInput
  const handleSubmit = async (message: string): Promise<void> => {
    if (currentChatId) {
      await handleSendMessage(message);
    } else if (message.trim() && !startChatMutation.isPending) {
      await handleStartChat(message);
    }
  };

  const handleExampleClick = async (question: string): Promise<void> => {
    if (!startChatMutation.isPending) {
      await handleStartChat(question);
    }
  };

  const handleCancel = async () => {
    // Reset mutations to cancel any pending requests
    startChatMutation.reset();
    sendMessageMutation.reset();

    // If we have an active chat that's generating, request cancellation from the backend
    if (currentChatId && chat?.status !== ChatStatus.USER_TURN) {
      try {
        await cancelGenerationMutation.mutateAsync({ chatId: currentChatId });
      } catch (err) {
        ErrorMonitoringService.captureError(err);
      }
    }
  };

  const sending = startChatMutation.isPending || sendMessageMutation.isPending;
  const isGenerating = loading;

  return (
    <>
      <div className={styles.content}>
        {/* Error Alert */}
        {(startChatMutation.isError || sendMessageMutation.isError) && (
          <Alert
            severity="error"
            onClose={() => {
              startChatMutation.reset();
              sendMessageMutation.reset();
            }}
            className={styles.errorAlert}
          >
            {startChatMutation.error?.message ||
              sendMessageMutation.error?.message ||
              "An error occurred"}
          </Alert>
        )}
        {/* Conditional Views */}
        <div className={styles.chatView}>
          {chat ? (
            <ChatByIdView chat={chat} />
          ) : (
            <NewChatView onExampleClick={handleExampleClick} />
          )}
        </div>
      </div>

      <div className={styles.inputContainer}>
        <ChatInputBox
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={sending || isGenerating}
        />
      </div>
    </>
  );
};
